import { DateRange } from '@angular/material/datepicker';
import { addDays, endOfDay, startOfDay, startOfWeek, subDays, subMonths } from 'date-fns';
import { fromZonedTime } from "date-fns-tz";

export interface DateRangePickerPreset<Date> {
  id: DateRangePickerPresetId,
  range: DateRange<Date>,
}

// snake_case allow to handle translation by ids
export enum DateRangePickerPresetId {
  tomorrow = 'tomorrow',
  nextWeek = 'nextWeek',
  next2Weeks = 'next2Weeks',
  last7Days = 'last7days',
  last2weeks = 'last2weeks',
  last3weeks = 'last3weeks',
  last30days = 'last30days',
  last2Months = 'last2Months',
  last3Months = 'last3Months',
}

export const DEFAULT_DATE_RANGE_PRESET_IDS = [
  DateRangePickerPresetId.last7Days,
  DateRangePickerPresetId.last2weeks,
  DateRangePickerPresetId.last3weeks,
  DateRangePickerPresetId.last30days,
  DateRangePickerPresetId.last2Months,
  DateRangePickerPresetId.last3Months
];

export function generatePresets(presetIds: DateRangePickerPresetId[], timezone: string): DateRangePickerPreset<Date>[] {
  return presetIds.map(presetId => generatePreset(presetId, timezone));
}

export function generatePreset(presetId: DateRangePickerPresetId, timezone: string): DateRangePickerPreset<Date> {
  return {
    id: presetId,
    range: generateRange(presetId, timezone)
  }
}

export function generateRange(presetId: DateRangePickerPresetId, timezone: string): DateRange<Date> {
  const now = fromZonedTime(new Date(), timezone); // beware of the timezone of the client
  switch (presetId) {
    case DateRangePickerPresetId.tomorrow:
      return nextNDays(1, now);
    case DateRangePickerPresetId.nextWeek:
      return nextNWeeks(1, now);
    case DateRangePickerPresetId.next2Weeks:
      return nextNWeeks(2, now);
    case DateRangePickerPresetId.last7Days:
      return lastNDays(7, now);
    case DateRangePickerPresetId.last2weeks:
      return lastNWeeks(2, now);
    case DateRangePickerPresetId.last3weeks:
      return lastNWeeks(3, now);
    case DateRangePickerPresetId.last30days:
      return lastNDays(30, now);
    case DateRangePickerPresetId.last2Months:
      return lastNMonths(2, now);
    case DateRangePickerPresetId.last3Months:
      return lastNMonths(3, now);
    default:
      throw Error('unknown preset ' + presetId);
  }
}

export function lastNDays(days: number, from: Date = new Date()): DateRange<Date> {
  const end = endOfDay(from);
  const start = startOfDay(subDays(end, days));
  return new DateRange(start, end);
}

export function nextNDays(days: number, from: Date = new Date()): DateRange<Date> {
  const tomorrow = startOfDay(addDays(from, 1));
  const endDate = endOfDay(addDays(from, days));
  return new DateRange(tomorrow, endDate);
}

export function lastNWeeks(weeks: number, from: Date = new Date()): DateRange<Date> {
  return lastNDays(weeks * 7, from);
}

/** For next N weeks, we take the first day of the week */
export function nextNWeeks(weeks: number, from: Date = new Date()): DateRange<Date> {
  const start = startOfWeek(addDays(from,  from.getDay() == 0 ? 1 : 7), {weekStartsOn: 1});
  const end = endOfDay(addDays(start, (weeks * 7) - 1));
  return new DateRange(start, end);
}

export function lastNMonths(months: number, from: Date = new Date()): DateRange<Date> {
  const start = startOfDay(subMonths(from, months));
  const end = endOfDay(from);
  return new DateRange(start, end);
}
